import moment from 'moment';
import { convertAntdDateToStorage, convertDateToAntdFormat, convertAntdRangeToStorage, convertDateRangeToAntdFormat } from './dateUtils';

/**
 * Utility functions for handling multiple date entries in quotations and invoices
 */

/**
 * Convert multiple date form values to storage format
 * @param {Object} formValues - Form values from Ant Design form
 * @param {string} documentType - Document type (purchase, rental, scaffolding_service)
 * @param {Array} dateEntries - Array of date entries with individual input types
 * @returns {Array} Array of processed date objects for storage
 */
export const convertMultipleDatesToStorage = (formValues, documentType, dateEntries) => {
  const processedDates = [];

  dateEntries.forEach(entry => {
    const entryId = entry.id;
    const inputType = entry.inputType;
    let dateData = { id: entryId, inputType };

    switch (documentType) {
      case 'purchase':
        if (inputType === 'date') {
          const deliveryDateValue = formValues[`deliveryDate_${entryId}`];
          if (deliveryDateValue && moment.isMoment(deliveryDateValue)) {
            dateData.value = convertAntdDateToStorage(deliveryDateValue);
            dateData.type = 'date';
          }
        } else if (inputType === 'months') {
          const monthsValue = formValues[`leadTimeMonths_${entryId}`];
          if (monthsValue) {
            dateData.value = monthsValue;
            dateData.type = 'months';
          }
        } else if (inputType === 'tbc') {
          dateData.value = 'TBC';
          dateData.type = 'tbc';
        }
        break;

      case 'scaffolding_service':
        if (inputType === 'date') {
          const serviceDateValue = formValues[`serviceDate_${entryId}`];
          if (serviceDateValue && moment.isMoment(serviceDateValue)) {
            dateData.value = convertAntdDateToStorage(serviceDateValue);
            dateData.type = 'date';
          }
        } else if (inputType === 'range') {
          const servicePeriodValue = formValues[`servicePeriod_${entryId}`];
          if (servicePeriodValue && Array.isArray(servicePeriodValue)) {
            const rangeData = convertAntdRangeToStorage(servicePeriodValue);
            dateData.startDate = rangeData.startDate;
            dateData.endDate = rangeData.endDate;
            dateData.type = 'range';
          }
        } else if (inputType === 'tbc') {
          dateData.value = 'TBC';
          dateData.type = 'tbc';
        }
        break;

      case 'rental':
      case 'rental_extension':
        if (inputType === 'date') {
          const rentalPeriodValue = formValues[`rentalPeriod_${entryId}`];
          if (rentalPeriodValue && Array.isArray(rentalPeriodValue)) {
            const rangeData = convertAntdRangeToStorage(rentalPeriodValue);
            dateData.startDate = rangeData.startDate;
            dateData.endDate = rangeData.endDate;
            dateData.type = 'range';
          }
        } else if (inputType === 'duration') {
          const durationValue = formValues[`rentalDuration_${entryId}`];
          const durationUnit = formValues[`rentalDurationUnit_${entryId}`];
          if (durationValue && durationUnit) {
            dateData.duration = durationValue;
            dateData.durationUnit = durationUnit;
            dateData.type = 'duration';
          }
        }
        break;
    }

    if (dateData.value || dateData.startDate || dateData.duration) {
      processedDates.push(dateData);
    }
  });

  return processedDates;
};

/**
 * Convert stored multiple dates back to form format
 * @param {Array} storedDates - Array of stored date objects with individual input types
 * @param {string} documentType - Document type
 * @returns {Object} Form values object and date entries array
 */
export const convertStoredDatesToForm = (storedDates, documentType) => {
  const formValues = {};
  const dateEntries = [];

  if (!storedDates || !Array.isArray(storedDates)) {
    return { 
      formValues, 
      dateEntries: [{ id: 1, key: Date.now(), inputType: getDefaultInputType(documentType) }] 
    };
  }

  storedDates.forEach((dateEntry, index) => {
    const entryId = dateEntry.id || (index + 1);
    const inputType = dateEntry.inputType || dateEntry.type;

    // Add to date entries array
    dateEntries.push({
      id: entryId,
      key: Date.now() + index,
      inputType: inputType
    });

    // Convert stored values to form values
    switch (documentType) {
      case 'purchase':
        if (inputType === 'date' && dateEntry.type === 'date' && dateEntry.value) {
          const convertedDate = convertDateToAntdFormat(dateEntry.value);
          if (convertedDate) {
            formValues[`deliveryDate_${entryId}`] = convertedDate;
          }
        } else if (inputType === 'months' && dateEntry.type === 'months' && dateEntry.value) {
          formValues[`leadTimeMonths_${entryId}`] = dateEntry.value;
        } else if (inputType === 'tbc') {
          formValues[`deliveryDateTbc_${entryId}`] = 'TBC';
        }
        break;

      case 'scaffolding_service':
        if (inputType === 'date' && dateEntry.type === 'date' && dateEntry.value) {
          const convertedDate = convertDateToAntdFormat(dateEntry.value);
          if (convertedDate) {
            formValues[`serviceDate_${entryId}`] = convertedDate;
          }
        } else if (inputType === 'range' && dateEntry.type === 'range' && dateEntry.startDate && dateEntry.endDate) {
          const convertedRange = convertDateRangeToAntdFormat(dateEntry.startDate, dateEntry.endDate);
          if (convertedRange) {
            formValues[`servicePeriod_${entryId}`] = convertedRange;
          }
        } else if (inputType === 'tbc') {
          formValues[`serviceDateTbc_${entryId}`] = 'TBC';
        }
        break;

      case 'rental':
      case 'rental_extension':
        if (inputType === 'date' && dateEntry.type === 'range' && dateEntry.startDate && dateEntry.endDate) {
          const convertedRange = convertDateRangeToAntdFormat(dateEntry.startDate, dateEntry.endDate);
          if (convertedRange) {
            formValues[`rentalPeriod_${entryId}`] = convertedRange;
          }
        } else if (inputType === 'duration' && dateEntry.type === 'duration' && dateEntry.duration && dateEntry.durationUnit) {
          formValues[`rentalDuration_${entryId}`] = dateEntry.duration;
          formValues[`rentalDurationUnit_${entryId}`] = dateEntry.durationUnit;
        }
        break;
    }
  });

  return { formValues, dateEntries };
};

/**
 * Format multiple dates for PDF display with mixed input types
 * @param {Array} storedDates - Array of stored date objects with individual input types
 * @param {string} documentType - Document type
 * @returns {string} Comma-separated formatted dates
 */
export const formatMultipleDatesForPDF = (storedDates, documentType) => {
  if (!storedDates || !Array.isArray(storedDates) || storedDates.length === 0) {
    return '';
  }

  const formattedDates = storedDates.map(dateEntry => {
    const inputType = dateEntry.inputType || dateEntry.type;

    switch (documentType) {
      case 'purchase':
        if (inputType === 'date' && dateEntry.type === 'date' && dateEntry.value) {
          return formatDateForDisplay(dateEntry.value);
        } else if (inputType === 'months' && dateEntry.type === 'months' && dateEntry.value) {
          return `${dateEntry.value} month${dateEntry.value > 1 ? 's' : ''}`;
        } else if (inputType === 'tbc') {
          return 'TBC';
        }
        break;

      case 'scaffolding_service':
        if (inputType === 'date' && dateEntry.type === 'date' && dateEntry.value) {
          return formatDateForDisplay(dateEntry.value);
        } else if (inputType === 'range' && dateEntry.type === 'range' && dateEntry.startDate && dateEntry.endDate) {
          return `${formatDateForDisplay(dateEntry.startDate)} - ${formatDateForDisplay(dateEntry.endDate)}`;
        } else if (inputType === 'tbc') {
          return 'TBC';
        }
        break;

      case 'rental':
      case 'rental_extension':
        if (inputType === 'date' && dateEntry.type === 'range' && dateEntry.startDate && dateEntry.endDate) {
          return `${formatDateForDisplay(dateEntry.startDate)} - ${formatDateForDisplay(dateEntry.endDate)}`;
        } else if (inputType === 'duration' && dateEntry.type === 'duration' && dateEntry.duration && dateEntry.durationUnit) {
          return `${dateEntry.duration} ${dateEntry.durationUnit}`;
        }
        break;
    }
    return null;
  }).filter(date => date !== null);

  return formattedDates.join(', ');
};

/**
 * Helper function to format date for display
 * @param {string} dateString - ISO date string
 * @param {string} format - Date format (default: DD/MM/YYYY)
 * @returns {string} Formatted date
 */
const formatDateForDisplay = (dateString, format = 'DD/MM/YYYY') => {
  if (!dateString) return '';
  
  const date = moment(dateString);
  if (!date.isValid()) return '';
  
  return date.format(format);
};

/**
 * Get default input type based on document type
 * @param {string} docType - Document type
 * @returns {string} Default input type
 */
export const getDefaultInputType = (docType) => {
  switch (docType) {
    case 'purchase':
      return 'date'; // Default to delivery date
    case 'scaffolding_service':
      return 'date'; // Default to service date
    case 'rental':
    case 'rental_extension':
      return 'date'; // Default to date range
    default:
      return 'date';
  }
};

/**
 * Generate date entries array from stored dates for component state
 * @param {Array} storedDates - Array of stored date objects
 * @param {string} documentType - Document type for default input type
 * @returns {Array} Array of date entries for component state
 */
export const generateDateEntriesFromStored = (storedDates, documentType) => {
  if (!storedDates || !Array.isArray(storedDates) || storedDates.length === 0) {
    return [{ id: 1, key: Date.now(), inputType: getDefaultInputType(documentType) }];
  }

  return storedDates.map((dateEntry, index) => ({
    id: dateEntry.id || (index + 1),
    key: Date.now() + index,
    inputType: dateEntry.inputType || dateEntry.type || getDefaultInputType(documentType)
  }));
};

/**
 * Check if multiple dates are enabled for backward compatibility
 * @param {Object} document - Document object (quotation or invoice)
 * @returns {boolean} Whether multiple dates should be enabled
 */
export const shouldEnableMultipleDates = (document) => {
  // Check if document has multiple date arrays
  const hasMultipleDates = 
    (document.multipleDates && Array.isArray(document.multipleDates)) ||
    (document.multipleLeadTimes && Array.isArray(document.multipleLeadTimes)) ||
    (document.multipleServiceDates && Array.isArray(document.multipleServiceDates)) ||
    (document.multipleRentalPeriods && Array.isArray(document.multipleRentalPeriods));

  return hasMultipleDates;
};
